import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "../CommonUIComponents/Modal/Modal";
import { Button } from "../CommonUIComponents/Button/Button";
import { useI18n } from "../../hooks/useI18n";
import { acceptConsent } from "../../store/slices/consentSlice";
import { consentContent } from "./consentText";
import "./ConsentModal.scss";

const BOTTOM_TOLERANCE_PX = 8;

export function ConsentModal() {
  const { t } = useI18n();
  const dispatch = useDispatch();
  const isSubmitting = useSelector((state) => state.consent.isSubmitting);
  const submitError = useSelector((state) => state.consent.submitError);

  const scrollRef = useRef(null);
  const doneRef = useRef(false);
  const [hasReadAll, setHasReadAll] = useState(false);

  // Enables the button when the content is not scrollable (fits without a
  // scrollbar) OR the user has scrolled to the end. `doneRef` guards against
  // doing extra work once the condition has been satisfied.
  const evaluate = useCallback(() => {
    if (doneRef.current) return true;
    const node = scrollRef.current;
    if (!node) return false;
    const { scrollTop, clientHeight, scrollHeight } = node;
    const isScrollable = scrollHeight - clientHeight > BOTTOM_TOLERANCE_PX;
    const reachedBottom =
      scrollTop + clientHeight >= scrollHeight - BOTTOM_TOLERANCE_PX;
    if (!isScrollable || reachedBottom) {
      doneRef.current = true;
      setHasReadAll(true);
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    // Poll until the condition is met, then stop. This is immune to the modal
    // mount/animation/font-loading timing issues that caused one-shot checks
    // (timeouts/observers) to run before layout settled and never re-check.
    if (evaluate()) return undefined;
    const intervalId = setInterval(() => {
      if (evaluate()) clearInterval(intervalId);
    }, 200);
    return () => clearInterval(intervalId);
  }, [evaluate]);

  const handleScroll = () => {
    evaluate();
  };

  const handleAgree = () => {
    if (!hasReadAll || isSubmitting) return;
    dispatch(acceptConsent());
  };

  const noop = () => {};

  return (
    <Modal
      show={true}
      onClose={noop}
      className="consent-modal"
      size="xl"
      header={t("consent_title")}
      body={
        <>
          <p className="consent-modal-intro">{t("consent_intro")}</p>
          <div
            ref={scrollRef}
            className="consent-modal-scroll"
            onScroll={handleScroll}
            tabIndex={0}
            role="region"
            aria-label={t("consent_title")}
          >
            <h3 className="consent-modal-title">{consentContent.title}</h3>
            <p className="consent-modal-subtitle">{consentContent.subtitle}</p>
            <p className="consent-modal-prompt">{consentContent.prompt}</p>
            <ul className="consent-modal-bullets">
              {consentContent.bullets.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
            <h4 className="consent-modal-declaration-heading">
              {consentContent.declarationHeading}
            </h4>
            <p className="consent-modal-declaration-text">
              {consentContent.declarationText}
            </p>
          </div>
          {!hasReadAll && (
            <p className="consent-modal-scroll-hint">
              {t("consent_scrollHint")}
            </p>
          )}
          {submitError && (
            <p className="consent-modal-error" role="alert">
              {t("consent_submitError")}
            </p>
          )}
        </>
      }
      footer={
        <div className="consent-modal-footer">
          <Button
            variant="primary"
            onClick={handleAgree}
            disabled={!hasReadAll || isSubmitting}
          >
            {isSubmitting ? t("consent_submitting") : t("consent_iAgree")}
          </Button>
        </div>
      }
    />
  );
}

export default ConsentModal;
